import {
  InjectionToken,
  Injectable,
  WritableSignal,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { ManagerOptions, Socket, SocketOptions, io } from 'socket.io-client';
import { environment } from '@env/environment';
import { AuthService } from '../auth/auth.service';
import { TenantContextService } from '../tenant/tenant-context.service';

/** Estado de un namespace, tal cual lo necesita mostrar la vista. */
export type ConnectionState = 'connecting' | 'connected' | 'reconnecting' | 'disconnected';

export type SocketFactory = (url: string, opts: Partial<ManagerOptions & SocketOptions>) => Socket;

/** Inyectable para poder sustituir socket.io en los tests. */
export const SOCKET_FACTORY = new InjectionToken<SocketFactory>('SOCKET_FACTORY', {
  providedIn: 'root',
  factory: () => (url, opts) => io(url, opts),
});

/** Reintentos del refresh ante fallos transitorios antes de darse por vencido. */
const MAX_REFRESH_RETRIES = 5;
const REFRESH_BACKOFF_BASE_MS = 1000;
const REFRESH_BACKOFF_MAX_MS = 30_000;

/**
 * Códigos del gateway que NO se arreglan reintentando: el contexto del usuario
 * tiene que cambiar primero. Reconectar ante ellos es un bucle garantizado.
 */
const PERMANENT_ERROR_CODES = new Set([
  'ACT_AS_TENANT_REQUIRED',
  'ACT_AS_TENANT_NOT_OPERABLE',
  'ACT_AS_TENANT_NOT_FOUND',
  'ACT_AS_JUSTIFICATION_REQUIRED',
  // Perfiles de estación (STATION_BASIC / DISPATCHER): su JWT trae `station_id` y
  // NO trae `tenant_id` — es correcto y esperado (ADR-0006 §5). Los dos gateways
  // tenant-scoped los rechazan por eso, y ningún reintento les va a agregar un
  // tenant: `alerts.gateway` responde NO_TENANT_OR_ROLE y `tracking.gateway`
  // TENANT_CONTEXT_REQUIRED (vía `resolveActor`), ambos con `disconnect(true)`.
  // Sin estos dos códigos acá, cada rechazo caía en la rama de reconexión y el
  // socket volvía a intentar en el acto: un bucle por namespace, en toda pantalla
  // del portal, durante toda la sesión.
  'NO_TENANT_OR_ROLE',
  'TENANT_CONTEXT_REQUIRED',
]);

/** Contexto vivo de un namespace. */
interface NamespaceCtx {
  readonly name: string;
  readonly sock: Socket;
  readonly state: WritableSignal<ConnectionState>;
  attempts: number;
  recovering: boolean;
  /** El gateway lo rechazó por contexto, no por red: no reconectar solo. */
  blocked: boolean;
}

/**
 * SocketService — abre 3 namespaces (tracking, dispatch, alerts) y los expone
 * como Signals.
 *
 * Auth en handshake: { token, tenant } — browsers no permiten headers custom en WS.
 * El payload se construye con un **callback**: socket.io lo pide en CADA intento,
 * así una reconexión automática nunca viaja con el JWT capturado al abrir.
 *
 * Act-as (SUPER_ADMIN): si hay un tenant operado, se agregan `actAsTenantId` +
 * `justification` al handshake para que el gateway una el socket a la sala del
 * tenant operado (mismo modelo que el snapshot HTTP — ver FGA-14 §2).
 *
 * Recuperación de sesión: el gateway valida `exp` (15 min) y responde
 * `error {code:'SESSION_EXPIRED'}` — que significa "refresca por HTTP y
 * reauth/reconecta", NO "no autorizado" (ws-auth.service.ts §códigos). El
 * portal refresca y, si el socket sigue abierto, emite `reauth` con el token
 * nuevo sin reconectar. Solo un rechazo genuino del refresh (INVALID_REFRESH)
 * cierra la sesión: un corte de red se reintenta con backoff.
 */
@Injectable({ providedIn: 'root' })
export class SocketService {
  private readonly auth = inject(AuthService);
  private readonly tenantCtx = inject(TenantContextService);
  private readonly socketFactory = inject(SOCKET_FACTORY);

  private readonly namespaces = new Map<string, NamespaceCtx>();

  readonly trackingState = signal<ConnectionState>('disconnected');
  readonly alertsState = signal<ConnectionState>('disconnected');

  readonly trackingConnected = computed(() => this.trackingState() === 'connected');

  /** Último delta del evento `position.update` del namespace tracking (sin validar). */
  readonly trackingPosition = signal<unknown>(null);
  /**
   * Último `device.command.updated` del namespace alerts, crudo y sin validar
   * (FGA-60). Handler con NOMBRE, igual que `position.update`.
   */
  readonly deviceCommandUpdate = signal<unknown>(null);

  /** `connectAll()` ya corrió: sin esto el effect abriría sockets antes de tiempo. */
  private started = false;

  constructor() {
    // Entrar o salir de act-as cambia si el gateway acepta estos namespaces. Este
    // servicio es root y sobrevive a toda la navegación, así que sin este effect
    // elegir tenant no reconectaba el mapa hasta un F5 completo.
    effect(() => {
      // Las dos condiciones se leen ANTES del guard: si se leyeran después, el
      // effect no quedaría suscrito a sus signals en la primera pasada.
      const tracking = this.canOpenTracking();
      const alerts = this.canOpenAlerts();
      if (!this.started) return;

      if (tracking) this.openTracking();
      else this.closeNamespace('tracking');

      if (alerts) this.openAlerts();
      else this.closeNamespace('alerts');
    });
  }

  /**
   * Estas dos condiciones son un ESPEJO de la regla de admisión de cada gateway, no
   * una lista de roles inventada acá. Si el backend cambia su regla, esto se
   * actualiza leyendo el gateway — inventar la regla en el portal es cómo se
   * terminan teniendo dos versiones de la misma verdad.
   *
   * `tracking` → `tracking.gateway` llama a `resolveActor()`: acepta si el JWT trae
   * `tenant_id` (cualquier rol), y si no lo trae sólo al SUPER_ADMIN que declare
   * act-as. Todo lo demás es `TENANT_CONTEXT_REQUIRED`/`ACT_AS_TENANT_REQUIRED`.
   */
  private canOpenTracking(): boolean {
    if (this.tenantCtx.tenantId() !== null) return true;
    return this.auth.isSuperAdmin() && this.tenantCtx.actingTenant() !== null;
  }

  /**
   * `alerts` → `alerts.gateway` exige `tenant_id` y hace UNA excepción: el
   * SUPER_ADMIN global, al que une a la sala `alerts:super-admin` (FGA-60). No
   * depende del act-as: el gateway no lo conoce, viaja sólo en headers HTTP.
   */
  private canOpenAlerts(): boolean {
    return this.tenantCtx.tenantId() !== null || this.auth.isSuperAdmin();
  }

  connectAll(): void {
    // Sin backend real no hay WS — evitar connect_error → refresh → token swap.
    if ((environment as typeof environment & { useMocks?: boolean }).useMocks) {
      console.log('[WS] useMocks=true — WebSocket desactivado en dev mock');
      return;
    }

    if (!this.auth.getAccessToken()) {
      console.warn('[WS] sin token de acceso — no se puede conectar');
      return;
    }
    console.log('[WS] abriendo namespaces', { wsUrl: environment.wsUrl });

    this.started = true;

    // Sólo lo que el gateway le aceptaría a este usuario. Abrir un namespace que va
    // a ser rechazado no es gratis: son handshakes contra el API, y un rechazo mal
    // clasificado se convierte en un bucle de reconexión (ver PERMANENT_ERROR_CODES).
    if (this.canOpenTracking()) this.openTracking();
    else console.log('[WS:tracking] sin tenant resoluble — no se abre');

    if (this.canOpenAlerts()) this.openAlerts();
    else console.log('[WS:alerts] sin tenant y sin super admin — no se abre');

    // `dispatch` NO se abre acá. Ninguna pantalla lee sus señales de este servicio,
    // y el único consumidor real —el turno de estación— abre su propio socket con
    // su propia suscripción por turno (`shift-transactions.store.ts`). Abrirlo desde
    // el layout era una conexión por usuario que no servía a nadie.
  }

  /** Abre `tracking` si no está ya vivo. Idempotente: el effect puede repetirlo. */
  private openTracking(): void {
    if (!this.puedeAbrir('tracking')) return;

    const tracking = this.connect('tracking', this.trackingState);
    tracking.sock.on('position.update', (d: unknown) => {
      this.trackingPosition.set(d);
    });
    this.warnOnUnknownEvents(tracking, ['position.update']);
  }

  /** Abre `alerts` si no está ya vivo. Idempotente, igual que `openTracking`. */
  private openAlerts(): void {
    if (!this.puedeAbrir('alerts')) return;

    const alerts = this.connect('alerts', this.alertsState);
    // El payload NO se valida acá: el schema vive en la feature de devices y core
    // no importa de features. Lo parsea quien lo consume (device-commands-panel).
    alerts.sock.on('device.command.updated', (d: unknown) => {
      this.deviceCommandUpdate.set(d);
    });
    this.warnOnUnknownEvents(alerts, ['device.command.updated']);
  }

  /**
   * Avisa de un evento que llegó sin handler con nombre. Es diagnóstico, **no** un
   * canal de datos: registra el NOMBRE del evento y nunca el payload, ni lo guarda
   * en ningún signal. Esa distinción es el punto del cambio — el `onAny` anterior
   * enrutaba todo a `lastAlert`/`lastPosition` y volcaba el payload completo a la
   * consola, así que un canal de debug terminó siendo el único camino de los
   * eventos reales al browser (y, en la sala global del super admin, volcaba a la
   * consola eventos de otros tenants).
   *
   * Sin esto un evento nuevo del backend desaparecería en silencio, que es peor que
   * el problema que vinimos a arreglar.
   */
  private warnOnUnknownEvents(ns: NamespaceCtx, conConsumidor: string[]): void {
    // `connected` lo emite el gateway al aceptar el socket; `error` ya tiene handler.
    const conocidos = new Set([...conConsumidor, 'connected', 'error']);
    ns.sock.onAny((event: string) => {
      if (conocidos.has(event)) return;
      console.warn(`[WS:${ns.name}] evento sin handler: ${event}`);
    });
  }

  /**
   * Deja el namespace listo para abrirse: `false` si ya existe y no está bloqueado,
   * o si no hay token.
   *
   * Basta con que EXISTA, no hace falta que ya esté conectado: socket.io reintenta
   * solo mientras está conectando, así que cerrarlo y recrearlo tira ese progreso.
   * Con el effect manejando dos namespaces esto pasó de teórico a real — cada
   * cambio de act-as recreaba el socket de `alerts` que aún no había conectado.
   * Un namespace bloqueado sí se recrea: quedó cerrado a propósito y el contexto
   * que lo bloqueaba acaba de cambiar.
   */
  private puedeAbrir(name: string): boolean {
    const vivo = this.namespaces.get(name);
    if (vivo && !vivo.blocked) return false;
    if (vivo) this.closeNamespace(name);
    return this.auth.getAccessToken() !== null;
  }

  /** Cierra un namespace y lo saca del registro (sin tocar los demás). */
  private closeNamespace(name: string): void {
    const ns = this.namespaces.get(name);
    if (!ns) return;
    ns.sock.disconnect();
    ns.state.set('disconnected');
    this.namespaces.delete(name);
  }

  disconnectAll(): void {
    for (const ns of this.namespaces.values()) {
      ns.sock.disconnect();
      ns.state.set('disconnected');
    }
    this.namespaces.clear();
  }

  /** Payload de auth del handshake; incluye act-as si un super_admin opera un tenant. */
  private buildAuth(): SocketAuth {
    const acting = this.tenantCtx.actingTenant();
    return {
      token: this.auth.getAccessToken() ?? '',
      tenant: this.tenantCtx.tenantSlug(),
      ...(acting ? { actAsTenantId: acting.id, justification: acting.justification } : {}),
    };
  }

  private connect(name: string, state: WritableSignal<ConnectionState>): NamespaceCtx {
    const sock = this.socketFactory(`${environment.wsUrl}/${name}`, {
      transports: ['websocket'],
      // Callback, no valor: se evalúa en cada intento de conexión.
      auth: (cb: (data: Record<string, unknown>) => void) =>
        cb(this.buildAuth() as unknown as Record<string, unknown>),
      reconnection: true,
      reconnectionDelay: 1000,
    });

    const ns: NamespaceCtx = {
      name,
      sock,
      state,
      attempts: 0,
      recovering: false,
      blocked: false,
    };
    this.namespaces.set(name, ns);
    state.set('connecting');

    sock.on('connect', () => {
      console.log(`[WS:${name}] ✅ conectado (id: ${sock.id})`);
      // Una conexión buena devuelve el presupuesto de reintentos: el próximo
      // corte vuelve a tener sus 5 oportunidades (antes era uno para siempre).
      ns.attempts = 0;
      ns.recovering = false;
      state.set('connected');
    });

    sock.on('disconnect', (reason: string) => {
      console.warn(`[WS:${name}] 🔌 desconectado — reason: ${reason}`);
      if (reason === 'io client disconnect') {
        state.set('disconnected');
        return;
      }
      // Rechazado por contexto: reconectar sólo repite el rechazo. Se queda
      // cerrado hasta que el contexto cambie (el effect de act-as lo reabre).
      if (ns.blocked) {
        state.set('disconnected');
        return;
      }
      state.set('reconnecting');
      // socket.io no reintenta solo cuando el cierre lo ordenó el servidor.
      if (reason === 'io server disconnect') sock.connect();
    });

    // Contrato del gateway: SESSION_EXPIRED = "refresca y reauth", no "fuera".
    sock.on('error', (payload: unknown) => {
      const code = (payload as { code?: string } | null)?.code;
      if (code === 'SESSION_EXPIRED') {
        console.log(`[WS:${name}] 🔑 sesión expirada — refrescando`);
        state.set('reconnecting');
        this.recoverSession(ns);
        return;
      }
      // Falta de contexto, no de red: el gateway va a rechazar cada intento
      // igual. Se marca para que el `disconnect` de abajo no reconecte.
      if (code && PERMANENT_ERROR_CODES.has(code)) {
        console.warn(`[WS:${name}] ⛔ ${code} — no se reintenta hasta cambiar de tenant`);
        ns.blocked = true;
        state.set('disconnected');
        sock.disconnect();
        return;
      }
      console.error(`[WS:${name}] ❌ error del servidor`, payload);
    });

    sock.on('connect_error', (err: Error) => {
      console.error(`[WS:${name}] ❌ connect_error: ${err.message}`);
      // `recoverSession` gasta una rotación del refresh token, y la rotación es
      // de un solo uso: dispararla en bucle por un namespace que el gateway
      // rechaza por contexto puede tumbar la sesión entera.
      if (ns.blocked) {
        state.set('disconnected');
        return;
      }
      state.set('reconnecting');
      this.recoverSession(ns);
    });

    return ns;
  }

  /**
   * Refresca el JWT por HTTP y devuelve el socket a la vida: `reauth` si sigue
   * abierto (el gateway inyecta el token sin reconectar) o reconexión si el
   * servidor ya lo cerró.
   */
  private recoverSession(ns: NamespaceCtx): void {
    if (ns.recovering) return;
    ns.recovering = true;

    this.auth.refresh().subscribe({
      next: (res) => {
        ns.recovering = false;
        ns.attempts = 0;
        if (ns.sock.connected) {
          console.log(`[WS:${ns.name}] 🔑 token renovado — reauth en caliente`);
          ns.sock.emit('reauth', { token: res.access_token });
        } else {
          console.log(`[WS:${ns.name}] 🔑 token renovado — reconectando`);
          ns.sock.connect();
        }
      },
      error: (err: unknown) => {
        ns.recovering = false;
        if (this.isGenuineRejection(err)) {
          console.error(`[WS:${ns.name}] 💥 refresh rechazado — cerrando sesión`);
          ns.state.set('disconnected');
          ns.sock.disconnect();
          this.auth.logout();
          return;
        }
        // Transitorio (red caída, 5xx): reintentar. Echar al operador del portal
        // por un corte de red es el peor final posible en campo.
        ns.attempts++;
        if (ns.attempts > MAX_REFRESH_RETRIES) {
          console.error(
            `[WS:${ns.name}] 💥 refresh sin éxito tras ${MAX_REFRESH_RETRIES} intentos`,
          );
          ns.state.set('disconnected');
          return;
        }
        ns.state.set('reconnecting');
        setTimeout(() => this.recoverSession(ns), this.backoffMs(ns.attempts));
      },
    });
  }

  /** Solo un rechazo del backend invalida la sesión; red y 5xx son transitorios. */
  private isGenuineRejection(err: unknown): boolean {
    const status = (err as { status?: number })?.status;
    if (status === undefined || status === 0) return false;
    return status < 500;
  }

  private backoffMs(attempt: number): number {
    return Math.min(REFRESH_BACKOFF_BASE_MS * 2 ** attempt, REFRESH_BACKOFF_MAX_MS);
  }
}

/** Payload de auth del handshake Socket.IO. */
interface SocketAuth {
  token: string;
  tenant: string | null;
  /** Solo super_admin en act-as: tenant operado + justificación auditada. */
  actAsTenantId?: string;
  justification?: string;
}
